import { z } from 'zod';

/**
 * Notifications for runs started by schedules and webhooks, sent through notification plugins
 * (Plugin rows with these keys, organization-wide or per project like every plugin). The chat
 * channels use an incoming-webhook URL; email uses the organization's own SMTP server. Webhook URLs
 * and the SMTP password are stored encrypted (Plugin.secretEnc) and never returned by the API.
 */

export const NOTIFY_CHANNELS = ['slack', 'teams', 'zoho-cliq', 'email'] as const;
export type NotifyChannel = (typeof NOTIFY_CHANNELS)[number];

export const NOTIFY_CHANNEL_NAMES: Record<NotifyChannel, string> = {
  slack: 'Slack',
  teams: 'Microsoft Teams',
  'zoho-cliq': 'Zoho Cliq',
  email: 'Email',
};

export function isNotifyChannel(key: string): key is NotifyChannel {
  return (NOTIFY_CHANNELS as readonly string[]).includes(key);
}

/** When a trigger sends notifications. FAILURES = when a run fails, and once when it passes again. */
export const NOTIFY_MODES = ['FAILURES', 'ALWAYS', 'OFF'] as const;
export type NotifyMode = (typeof NOTIFY_MODES)[number];

// ---------------------------------------------------------------- where chat webhooks may point

/**
 * Hosts each chat service uses for incoming webhooks. Anything else is refused, so a plugin can't
 * make 1QA send requests to internal services.
 */
const WEBHOOK_HOSTS: Record<Exclude<NotifyChannel, 'email'>, RegExp> = {
  slack: /^hooks\.slack(-gov)?\.com$/,
  // Teams Workflows (Power Automate) and the older Office 365 connectors.
  teams: /^([a-z0-9-]+\.)*(webhook\.office\.com|logic\.azure\.com|powerautomate\.com|powerplatform\.com|api\.powerplatform\.com)$/,
  'zoho-cliq': /^cliq\.zoho\.(com|eu|in|com\.au|jp|com\.cn|sa|ca)$/,
};

const WEBHOOK_EXAMPLES: Record<Exclude<NotifyChannel, 'email'>, string> = {
  slack: 'https://hooks.slack.com/services/…',
  teams: 'https://….webhook.office.com/… or a Teams Workflows URL (….logic.azure.com / ….powerplatform.com)',
  'zoho-cliq': 'https://cliq.zoho.com/api/v2/channelsbyname/<channel>/message?zapikey=…',
};

/** Why a webhook URL can't be used for a channel, or null when it is fine. */
export function webhookUrlProblem(channel: Exclude<NotifyChannel, 'email'>, url: string): string | null {
  let u: URL;
  try {
    u = new URL(url);
  } catch {
    return 'That is not a web address';
  }
  if (u.protocol !== 'https:') return 'The webhook address must start with https://';
  if (u.username || u.password || (u.port && u.port !== '443')) return 'The webhook address must not contain a user name, password or port';
  if (!WEBHOOK_HOSTS[channel].test(u.hostname.toLowerCase())) {
    return `This is not a ${NOTIFY_CHANNEL_NAMES[channel]} webhook address (expected ${WEBHOOK_EXAMPLES[channel]})`;
  }
  return null;
}

// ---------------------------------------------------------------- settings

/** SMTP ports allowed for email (keeps the email setting from reaching other internal services). */
export const SMTP_PORTS = [25, 465, 587, 2525] as const;

export const emailConfigSchema = z.object({
  /** Who gets the email. */
  recipients: z.array(z.string().trim().email()).min(1, 'Add at least one recipient').max(20),
  host: z
    .string()
    .trim()
    .min(1, 'Enter the SMTP server')
    .max(253)
    .regex(/^[A-Za-z0-9.-]+$/, 'Enter a host name such as smtp.example.com'),
  port: z
    .number()
    .int()
    .refine((p) => (SMTP_PORTS as readonly number[]).includes(p), `Use port ${SMTP_PORTS.join(', ')}`),
  /** true: TLS from the start (port 465); false: STARTTLS when the server offers it. */
  secure: z.boolean().default(false),
  username: z.string().trim().max(200).optional(),
  from: z.string().trim().email('Enter the sender address'),
});
export type EmailConfig = z.infer<typeof emailConfigSchema>;

/** Settings of a notification plugin (config) and what its secret is. */
export function notifyConfigProblem(channel: NotifyChannel, config: unknown): string | null {
  if (channel !== 'email') return null;
  const r = emailConfigSchema.safeParse(config);
  return r.success ? null : (r.error.issues[0]?.message ?? 'Check the email settings');
}

export const NOTIFY_SECRET_LABEL: Record<NotifyChannel, string> = {
  slack: 'Webhook URL',
  teams: 'Webhook URL',
  'zoho-cliq': 'Webhook URL',
  email: 'SMTP password',
};

// ---------------------------------------------------------------- the message

export interface RunNotice {
  /** FAILED / ERROR / PASSED / CANCELLED. */
  status: string;
  /** The run passed after the previous one failed. */
  recovered: boolean;
  triggerName: string;
  triggerKind: 'SCHEDULE' | 'WEBHOOK';
  projectName: string;
  /** "flow “Release gate”" or "group “Smoke”". */
  target: string;
  passed: number;
  failed: number;
  total: number;
  /** Names of failed tests or steps (a few). */
  failures: string[];
  durationMs?: number;
  reportUrl: string;
  /** "test": sent from the Plugins page to check the setup. */
  test?: boolean;
}

export interface NoticeText {
  title: string;
  /** Short lines (no markup). */
  lines: string[];
  /** good / bad / neutral, for colours in chat cards. */
  tone: 'good' | 'bad' | 'neutral';
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

/** The words of a notification, shared by every channel. */
export function noticeText(n: RunNotice): NoticeText {
  const how = n.triggerKind === 'SCHEDULE' ? 'Scheduled run' : 'Webhook run';
  const failedNow = n.status === 'FAILED' || n.status === 'ERROR';
  const title = n.test
    ? '1QA test notification'
    : failedNow
      ? `${how} ${n.status === 'ERROR' ? 'could not finish' : 'failed'}: ${n.triggerName}`
      : n.recovered
        ? `Passing again: ${n.triggerName}`
        : `${how} ${n.status === 'PASSED' ? 'passed' : n.status.toLowerCase()}: ${n.triggerName}`;
  const lines = n.test
    ? [`Notifications from 1QA will arrive here.`, `Project: ${n.projectName}`]
    : [
        `Project: ${n.projectName} · ${n.target}`,
        `Result: ${n.passed} of ${plural(n.total, 'test')} passed${n.failed ? `, ${n.failed} failed` : ''}${n.durationMs ? ` · ${Math.max(1, Math.round(n.durationMs / 1000))} s` : ''}`,
        ...(n.failures.length ? [`Failed: ${n.failures.slice(0, 5).join(', ')}${n.failures.length > 5 ? ` and ${n.failures.length - 5} more` : ''}`] : []),
      ];
  return { title, lines, tone: n.test ? 'neutral' : failedNow ? 'bad' : n.status === 'PASSED' ? 'good' : 'neutral' };
}

/** What a flow's Notify step sends (see flows.ts). */
export interface FlowNotice {
  flowName: string;
  projectName: string;
  collectionName?: string | null;
  /** The user's title; default: the flow and its result. */
  subject?: string;
  /** The user's own text (may have several lines). */
  message?: string;
  /** Results to show; omitted = only the message. */
  results?: {
    /** "the flow so far" or "the step before". */
    scope: 'flow' | 'previous';
    total: number;
    passed: number;
    failed: number;
    /** Names of failed tests (a few). */
    failures: string[];
  };
}

/** The words of a flow notification, shared by every channel. */
export function flowNoticeText(n: FlowNotice): NoticeText {
  const r = n.results;
  const bad = r ? r.failed : 0;
  const outcome = !r ? 'update' : r.total === 0 ? 'no results yet' : bad ? `${bad} of ${plural(r.total, 'test')} failed` : `all ${plural(r.total, 'test')} passed`;
  const title = n.subject || `Flow “${n.flowName}”: ${outcome}`;
  const lines = [
    ...(n.message ? n.message.split(/\r?\n/).map((l) => l.trimEnd()) : []),
    `Project: ${n.projectName} · flow “${n.flowName}”${n.collectionName ? ` (${n.collectionName})` : ''}`,
    ...(r
      ? [
          `Result (${r.scope === 'flow' ? 'the flow so far' : 'the step before'}): ${r.passed} of ${plural(r.total, 'test')} passed${r.failed ? `, ${r.failed} failed` : ''}`,
          ...(r.failures.length ? [`Failed: ${r.failures.slice(0, 5).join(', ')}${r.failures.length > 5 ? ` and ${r.failures.length - 5} more` : ''}`] : []),
        ]
      : []),
  ];
  return { title, lines, tone: !r || r.total === 0 ? 'neutral' : bad ? 'bad' : 'good' };
}

/** Whether a finished triggered run should notify under a mode. */
export function shouldNotify(mode: NotifyMode, status: string, previousStatus: string | null): { send: boolean; recovered: boolean } {
  const failed = status === 'FAILED' || status === 'ERROR';
  const recovered = status === 'PASSED' && (previousStatus === 'FAILED' || previousStatus === 'ERROR');
  if (mode === 'OFF' || status === 'CANCELLED') return { send: false, recovered };
  if (mode === 'ALWAYS') return { send: true, recovered };
  return { send: failed || recovered, recovered };
}

// ---------------------------------------------------------------- payloads per channel

/** Slack incoming webhook body. */
export function slackPayload(t: NoticeText, reportUrl: string) {
  return {
    text: `${t.title} - ${reportUrl}`,
    blocks: [
      { type: 'header', text: { type: 'plain_text', text: t.title.slice(0, 150) } },
      { type: 'section', text: { type: 'mrkdwn', text: t.lines.map(escapeSlack).join('\n') } },
      { type: 'actions', elements: [{ type: 'button', text: { type: 'plain_text', text: 'Open report' }, url: reportUrl }] },
    ],
  };
}
const escapeSlack = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/** Teams (Workflows or connector) body: an Adaptive Card. */
export function teamsPayload(t: NoticeText, reportUrl: string) {
  return {
    type: 'message',
    attachments: [
      {
        contentType: 'application/vnd.microsoft.card.adaptive',
        content: {
          $schema: 'http://adaptivecards.io/schemas/adaptive-card.json',
          type: 'AdaptiveCard',
          version: '1.4',
          body: [
            { type: 'TextBlock', text: t.title, weight: 'Bolder', size: 'Medium', wrap: true, color: t.tone === 'bad' ? 'Attention' : t.tone === 'good' ? 'Good' : 'Default' },
            ...t.lines.map((l) => ({ type: 'TextBlock', text: l, wrap: true, spacing: 'Small' })),
          ],
          actions: [{ type: 'Action.OpenUrl', title: 'Open report', url: reportUrl }],
        },
      },
    ],
  };
}

/** Zoho Cliq incoming webhook body. */
export function cliqPayload(t: NoticeText, reportUrl: string) {
  return {
    text: t.lines.join('\n'),
    card: { title: t.title.slice(0, 100), theme: 'modern-inline' },
    buttons: [{ label: 'Open report', type: t.tone === 'bad' ? '-' : '+', action: { type: 'open.url', data: { web: reportUrl } } }],
  };
}

const escapeHtml = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** Email subject, plain text and HTML. */
export function emailContent(t: NoticeText, reportUrl: string) {
  const color = t.tone === 'bad' ? '#be123c' : t.tone === 'good' ? '#047857' : '#334155';
  return {
    subject: `1QA: ${t.title}`,
    text: [t.title, '', ...t.lines, '', `Report: ${reportUrl}`].join('\n'),
    html:
      `<div style="font-family:system-ui,sans-serif;font-size:14px;color:#0f172a">` +
      `<h2 style="margin:0 0 12px;color:${color};font-size:18px">${escapeHtml(t.title)}</h2>` +
      t.lines.map((l) => `<p style="margin:0 0 6px">${escapeHtml(l)}</p>`).join('') +
      `<p style="margin:16px 0 0"><a href="${escapeHtml(reportUrl)}" style="background:#4f46e5;color:#fff;padding:8px 14px;border-radius:8px;text-decoration:none">Open report</a></p>` +
      `</div>`,
  };
}
